/**
 * Prop-layer rendering — draws props as small coloured rectangles with a
 * label (procedural fallback per Q-C8 / CLIENT_DESIGN §4.6).
 *
 * The renderer places one rectangle per in-viewport prop on top of the
 * floor-tile diamond at the prop's position. Real sprite art replaces
 * this in Phase 4 polish.
 */

import { Container, Graphics, Text } from 'pixi.js';

import type { WorldModel } from '../world/model';
import {
  tileToIso,
  TILE_HEIGHT_PX,
  TILE_WIDTH_PX,
  visibleTileRange,
  type CameraState,
} from './iso';
import { blightDesaturate, propKindColor, spoilageTint, SPOILAGE_STAGES } from './palette';
import type { SceneLayers } from './scene';

const PROP_BOX_WIDTH = 40;
const PROP_BOX_HEIGHT = 48;

interface CropAppearance {
  color: number;
  label: string;
  heightScale: number;
}

const CROP_STAGES: CropAppearance[] = [
  { color: 0x6b5028, label: 'tilled',   heightScale: 0.5 },
  { color: 0x7a6030, label: 'seeded',   heightScale: 0.5 },
  { color: 0x4a8c3a, label: 'sprout',   heightScale: 0.65 },
  { color: 0x3a7c2a, label: 'growing',  heightScale: 0.8 },
  { color: 0xc8a832, label: 'harvest!', heightScale: 1.0 },
  { color: 0x5a4a30, label: 'withered', heightScale: 0.7 },
];

export function cropStageAppearance(visualState: number): CropAppearance | null {
  return CROP_STAGES[visualState] ?? null;
}

export interface PropRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ) => void;
  destroy: () => void;
}

export function createPropRenderer(layers: SceneLayers): PropRenderer {
  // All props share one `Container`; each prop is a small Graphics + Text
  // pair. We recycle Container children rather than destroy+recreate each
  // frame so the allocator doesn't churn.
  const root = new Container({ label: 'props-root' });
  layers.propLayer.addChild(root);

  // Simple pool keyed by PropId. When a prop leaves the viewport, its
  // children hide (visible = false) but stay allocated.
  const pool = new Map<number, { graphics: Graphics; label: Text }>();

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ): void => {
    if (model.props.size === 0) {
      // Hide everything currently in the pool; keep allocation for next frame.
      for (const entry of pool.values()) {
        entry.graphics.visible = false;
        entry.label.visible = false;
      }
      return;
    }

    const r = visibleTileRange(cam, viewportW, viewportH, 2);
    const seen = new Set<number>();

    for (const prop of model.props.values()) {
      const [px, py] = prop.position;
      if (px < r.minX || px > r.maxX || py < r.minY || py > r.maxY) continue;

      let entry = pool.get(prop.id);
      if (!entry) {
        const graphics = new Graphics();
        const label = new Text({
          text: '',
          style: {
            fill: 0xf0f0f0,
            fontSize: 9,
            fontFamily: 'sans-serif',
          },
        });
        label.anchor.set(0.5, 1);
        root.addChild(graphics, label);
        entry = { graphics, label };
        pool.set(prop.id, entry);
      }

      const iso = tileToIso(px, py);
      const isSpoiling = prop.machine_state !== null
        && SPOILAGE_STAGES.has(prop.machine_state);
      const isCrop = !isSpoiling
        && (prop.machine_state !== null || prop.visual_state > 0);
      const isBlighted = prop.machine_state === 'blighted';
      const stage = isCrop ? cropStageAppearance(prop.visual_state) : null;

      let color: number;
      if (isSpoiling) {
        color = spoilageTint(propKindColor(prop.kind), prop.machine_state);
      } else if (isBlighted && stage) {
        color = blightDesaturate(stage.color);
      } else {
        color = stage ? stage.color : propKindColor(prop.kind);
      }
      const boxH = stage ? PROP_BOX_HEIGHT * stage.heightScale : PROP_BOX_HEIGHT;
      const boxTopY = iso.y + TILE_HEIGHT_PX / 2 - boxH;
      entry.graphics.clear();
      entry.graphics.rect(
        iso.x - PROP_BOX_WIDTH / 2,
        boxTopY,
        PROP_BOX_WIDTH,
        boxH,
      );
      entry.graphics.fill({ color, alpha: 1 });
      entry.graphics.stroke({ color: 0x000000, width: 1, alpha: 0.4 });
      entry.graphics.visible = true;

      entry.label.text = isBlighted
        ? 'blighted'
        : isSpoiling ? (prop.machine_state ?? '')
        : stage ? stage.label : `k${prop.kind}`;
      entry.label.position.set(iso.x, boxTopY);
      entry.label.visible = true;

      seen.add(prop.id);
    }

    // Hide entries whose props left the viewport this frame.
    for (const [id, entry] of pool) {
      if (seen.has(id)) continue;
      entry.graphics.visible = false;
      entry.label.visible = false;
    }

    // Tile-width used via the iso constants — keep the unused import
    // suppressor happy in case bundler tree-shakes loose.
    void TILE_WIDTH_PX;
  };

  return {
    render,
    destroy: () => {
      for (const entry of pool.values()) {
        entry.graphics.destroy();
        entry.label.destroy();
      }
      pool.clear();
      root.destroy();
    },
  };
}
